import { Currency, CurrencyAmount, Token, Trade, TradeType, TokenAmount, JSBI } from '@uniswap/sdk'
import { useMemo } from 'react'
import { isTaxToken, getTotalBuyTax, getTotalSellTax } from '../utils/dynamicTaxToken'

/**
 * Custom trade class that accounts for tax tokens
 */
export class TaxAwareTrade extends Trade {
  public readonly taxAdjustedOutputAmount: CurrencyAmount
  public readonly taxAdjustedInputAmount: CurrencyAmount
  public readonly buyTaxPercent: number
  public readonly sellTaxPercent: number

  constructor(
    route: Trade['route'],
    amount: CurrencyAmount,
    tradeType: TradeType,
    inputToken?: Token,
    outputToken?: Token
  ) {
    super(route, amount, tradeType)

    // Calculate tax percentages
    this.buyTaxPercent =
      outputToken && isTaxToken(outputToken) ? parseInt(getTotalBuyTax(outputToken).toFixed(4)) / 100 : 0

    this.sellTaxPercent =
      inputToken && isTaxToken(inputToken) ? parseInt(getTotalSellTax(inputToken).toFixed(4)) / 100 : 0

    // Calculate tax-adjusted amounts
    if (tradeType === TradeType.EXACT_INPUT) {
      this.taxAdjustedInputAmount = this.inputAmount

      if (outputToken && isTaxToken(outputToken)) {
        // When buying tax token, output will be reduced by buy tax
        const buyTax = getTotalBuyTax(outputToken)
        const afterTaxMultiplier = JSBI.subtract(JSBI.BigInt(10000), buyTax.numerator)
        const adjustedAmount = JSBI.divide(JSBI.multiply(this.outputAmount.raw, afterTaxMultiplier), JSBI.BigInt(10000))

        this.taxAdjustedOutputAmount =
          this.outputAmount instanceof TokenAmount
            ? new TokenAmount(this.outputAmount.token, adjustedAmount)
            : CurrencyAmount.ether(adjustedAmount)
      } else {
        this.taxAdjustedOutputAmount = this.outputAmount
      }
    } else {
      // EXACT_OUTPUT
      this.taxAdjustedOutputAmount = this.outputAmount

      if (inputToken && isTaxToken(inputToken)) {
        // When selling tax token, need more input to account for sell tax
        const sellTax = getTotalSellTax(inputToken)
        const beforeTaxMultiplier = JSBI.BigInt(10000)
        const afterTaxMultiplier = JSBI.subtract(JSBI.BigInt(10000), sellTax.numerator)
        const adjustedAmount = JSBI.divide(JSBI.multiply(this.inputAmount.raw, beforeTaxMultiplier), afterTaxMultiplier)

        this.taxAdjustedInputAmount =
          this.inputAmount instanceof TokenAmount
            ? new TokenAmount(this.inputAmount.token, adjustedAmount)
            : CurrencyAmount.ether(adjustedAmount)
      } else {
        this.taxAdjustedInputAmount = this.inputAmount
      }
    }
  }

  /**
   * Get the execution price accounting for taxes
   */
  get taxAdjustedExecutionPrice() {
    return this.taxAdjustedOutputAmount.divide(this.taxAdjustedInputAmount)
  }

  /**
   * Check if this trade involves any tax tokens
   */
  get involvesTaxToken(): boolean {
    const inputToken = this.inputAmount.currency instanceof Token ? this.inputAmount.currency : undefined
    const outputToken = this.outputAmount.currency instanceof Token ? this.outputAmount.currency : undefined

    return !!(inputToken && isTaxToken(inputToken)) || !!(outputToken && isTaxToken(outputToken))
  }
}

/**
 * Enhanced hook for exact input trades that accounts for tax tokens
 */
export function useTaxAwareTradeExactIn(
  currencyAmountIn?: CurrencyAmount,
  currencyOut?: Currency
): TaxAwareTrade | null {
  return useMemo(() => {
    if (!currencyAmountIn || !currencyOut) return null

    // For now, we'll need to integrate with the existing trade logic
    // This is a simplified version - in practice, you'd want to modify the existing useTradeExactIn
    // to return TaxAwareTrade instances

    const inputToken = currencyAmountIn.currency instanceof Token ? currencyAmountIn.currency : undefined
    const outputToken = currencyOut instanceof Token ? currencyOut : undefined

    // Check if either token is a tax token
    if (!inputToken || !outputToken || (!isTaxToken(inputToken) && !isTaxToken(outputToken))) {
      return null // Fall back to regular trade logic
    }

    // This would need to be integrated with the existing pair finding and route calculation logic
    // For now, return null to indicate we need the regular trade logic
    return null
  }, [currencyAmountIn, currencyOut])
}

/**
 * Enhanced hook for exact output trades that accounts for tax tokens
 */
export function useTaxAwareTradeExactOut(
  currencyIn?: Currency,
  currencyAmountOut?: CurrencyAmount
): TaxAwareTrade | null {
  return useMemo(() => {
    if (!currencyIn || !currencyAmountOut) return null

    const inputToken = currencyIn instanceof Token ? currencyIn : undefined
    const outputToken = currencyAmountOut.currency instanceof Token ? currencyAmountOut.currency : undefined

    // Check if either token is a tax token
    if (!inputToken || !outputToken || (!isTaxToken(inputToken) && !isTaxToken(outputToken))) {
      return null // Fall back to regular trade logic
    }

    // This would need to be integrated with the existing pair finding and route calculation logic
    return null
  }, [currencyIn, currencyAmountOut])
}

/**
 * Helper function to determine if a trade should use fee-on-transfer methods
 */
export function shouldUseFeeOnTransferMethods(trade: Trade | null): boolean {
  if (!trade) return false

  const inputToken = trade.inputAmount.currency instanceof Token ? trade.inputAmount.currency : undefined
  const outputToken = trade.outputAmount.currency instanceof Token ? trade.outputAmount.currency : undefined

  // Always use fee-on-transfer methods if any token in the path is a tax token
  if (inputToken && isTaxToken(inputToken)) return true
  if (outputToken && isTaxToken(outputToken)) return true

  // Check intermediate tokens in multi-hop trades
  for (const pair of trade.route.pairs) {
    if (isTaxToken(pair.token0) || isTaxToken(pair.token1)) {
      return true
    }
  }

  return false
}
